// src/pages/LoginPage/LoginPage.tsx
import React, { useState, useEffect } from 'react';
import { Form, Input, Alert, Tabs, message } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined } from '@ant-design/icons';
import { useAuth } from '../../auth/AuthProvider';
import { LoginInput } from '../../auth/types';
import { useBrand } from '../../utils/context/BrandContext';
import { Button } from '../../components';
import { ButtonTypes, useTranslation } from '../../utils';
import { loginWithAzureAD } from '../../auth/azureAd';
import { initializeGoogleSignIn, triggerGoogleSignIn } from '../../auth/googleAuth';

import './LoginPage.scss';

const SAVED_USERNAME_KEY = 'login_saved_username';

type LoginMethod = 'password' | 'magic' | 'azure' | 'google';

const LoginPage: React.FC = () => {
  const [form] = Form.useForm();
  const { login, loginWithProvider, requestMagicLink, state } = useAuth();
  const { brandConfig } = useBrand();
  const translate = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginMethod, setLoginMethod] = useState<LoginMethod>('password');
  const [magicLinkEmail, setMagicLinkEmail] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  // Restore username from sessionStorage on mount (survives component remount)
  useEffect(() => {
    const savedUsername = sessionStorage.getItem(SAVED_USERNAME_KEY);
    if (savedUsername) {
      form.setFieldsValue({
        username: savedUsername,
        password: '',
      });
    }
  }, [form]);

  // Initialize Google Sign-In on mount
  useEffect(() => {
    if (process.env.REACT_APP_GOOGLE_CLIENT_ID) {
      initializeGoogleSignIn(
        async (idToken: string) => {
          try {
            setIsSubmitting(true);
            await loginWithProvider('GOOGLE', idToken);
          } catch (error) {
            console.error('Google login failed:', error);
          } finally {
            setIsSubmitting(false);
          }
        },
        (error) => {
          console.error('Google Sign-In initialization failed:', error);
        },
      );
    }
  }, [loginWithProvider]);

  const handleSubmit = async (values: LoginInput) => {
    // Save username to sessionStorage before submitting (survives component remount)
    sessionStorage.setItem(SAVED_USERNAME_KEY, values.username);
    setIsSubmitting(true);
    try {
      await login(values);
      // Clear saved username on successful login
      sessionStorage.removeItem(SAVED_USERNAME_KEY);
    } catch (error) {
      // Error is already handled in the auth provider
      console.error('Login failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMagicLinkRequest = async () => {
    if (!magicLinkEmail) {
      message.error('Please enter your email address');
      return;
    }

    setIsSubmitting(true);
    try {
      await requestMagicLink(magicLinkEmail);
      setMagicLinkSent(true);
      message.success('Check your email for the login link!');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send magic link';
      message.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAzureLogin = async () => {
    setIsSubmitting(true);
    try {
      const idToken = await loginWithAzureAD();
      await loginWithProvider('AZURE', idToken);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Azure AD login failed';
      message.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleLogin = () => {
    try {
      triggerGoogleSignIn();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Google login failed';
      message.error(errorMessage);
    }
  };

  const appName = brandConfig.name === 'memo' ? 'MemoWeb' : 'OffiWeb';

  const tabItems = [
    {
      key: 'password',
      label: 'Password',
    },
    ...(process.env.REACT_APP_AZURE_AD_CLIENT_ID
      ? [
          {
            key: 'azure',
            label: 'Azure AD',
          },
        ]
      : []),
    ...(process.env.REACT_APP_GOOGLE_CLIENT_ID
      ? [
          {
            key: 'google',
            label: 'Google',
          },
        ]
      : []),
    {
      key: 'magic',
      label: 'Magic Link',
    },
  ];

  return (
    <div className="login-page">
      <div className="login-page__container">
        <div className="login-page__header">
          <div className="login-page__logo">
            <img
              src={brandConfig.logo || '/images/new-icons/logo.svg'}
              alt={appName}
              className="login-page__logo-image"
            />
          </div>

          <p className="login-page__subtitle">{translate('login.subtitle')}</p>
        </div>

        <Tabs
          activeKey={loginMethod}
          onChange={(key) => {
            setLoginMethod(key as LoginMethod);
            setMagicLinkSent(false);
            form.resetFields();
          }}
          items={tabItems}
          className="login-page__tabs"
        />

        {state.error && (
          <Alert message={state.error} type="error" showIcon className="login-page__error" />
        )}

        {/* Password Login */}
        {loginMethod === 'password' && (
          <Form
            form={form}
            name="login"
            className="login-page__form"
            onFinish={handleSubmit}
            layout="vertical"
            size="large"
          >
            <Form.Item
              name="username"
              label={translate('login.usernameOrEmail')}
              rules={[{ required: true, message: translate('form.isrequired') }]}
            >
              <Input
                prefix={<UserOutlined className="login-page__input-icon" />}
                placeholder={translate('login.usernameOrEmailPlaceholder')}
                autoComplete="username"
                autoFocus
              />
            </Form.Item>

            <Form.Item
              name="password"
              label={translate('login.password')}
              rules={[{ required: true, message: translate('form.isrequired') }]}
            >
              <Input.Password
                prefix={<LockOutlined className="login-page__input-icon" />}
                placeholder={translate('login.passwordPlaceholder')}
                autoComplete="current-password"
              />
            </Form.Item>

            <Form.Item className="login-page__submit">
              <Button
                text={translate('login.submit')}
                type={ButtonTypes.Blue}
                htmlType="submit"
                disabled={isSubmitting}
                loading={isSubmitting}
                block={true}
              />
            </Form.Item>
          </Form>
        )}

        {/* Magic Link */}
        {loginMethod === 'magic' && (
          <Form
            className="login-page__form"
            onFinish={handleMagicLinkRequest}
            layout="vertical"
            size="large"
          >
            {magicLinkSent ? (
              <div className="login-page__magic-link-sent">
                <Alert
                  message="Check your email!"
                  description="We've sent you a magic link. Click the link in your email to log in."
                  type="success"
                  showIcon
                />
                <div style={{ marginTop: '16px' }}>
                  <Button
                    text="Send another link"
                    type={ButtonTypes.Blue}
                    onClick={() => {
                      setMagicLinkSent(false);
                      setMagicLinkEmail('');
                    }}
                    block={true}
                  />
                </div>
              </div>
            ) : (
              <>
                <Form.Item label="Email" required>
                  <Input
                    prefix={<MailOutlined className="login-page__input-icon" />}
                    placeholder="Enter your email address"
                    type="email"
                    value={magicLinkEmail}
                    onChange={(e) => {
                      setMagicLinkEmail(e.target.value);
                    }}
                    autoComplete="email"
                    autoFocus
                    size="large"
                  />
                </Form.Item>
                <Form.Item className="login-page__submit">
                  <Button
                    text="Send Magic Link"
                    type={ButtonTypes.Blue}
                    htmlType="submit"
                    disabled={isSubmitting || !magicLinkEmail}
                    loading={isSubmitting}
                    block={true}
                  />
                </Form.Item>
              </>
            )}
          </Form>
        )}

        {/* Azure AD */}
        {loginMethod === 'azure' && (
          <div className="login-page__form">
            <Form.Item className="login-page__submit">
              <Button
                text="Login with Azure AD"
                type={ButtonTypes.Blue}
                onClick={handleAzureLogin}
                disabled={isSubmitting}
                loading={isSubmitting}
                block={true}
              />
            </Form.Item>
          </div>
        )}

        {/* Google */}
        {loginMethod === 'google' && (
          <div className="login-page__form">
            <Form.Item className="login-page__submit">
              <Button
                text="Login with Google"
                type={ButtonTypes.Blue}
                onClick={handleGoogleLogin}
                disabled={isSubmitting}
                loading={isSubmitting}
                block={true}
              />
            </Form.Item>
          </div>
        )}

        <div className="login-page__footer">
          <p className="login-page__copyright">
            © {new Date().getFullYear()} {appName}. {translate('login.allRightsReserved')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
